import { useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingContext from '../../../../context/loadingContext/LoadingContext';
import useAnalyticsStore from '../../../../store/analyticsStore';
import useHomeStore from '../../../../store/homeStore';
import useLegendStore from '../../../../store/legendStore';
import { useActiveMarker, useMarkerStore } from '../../../../store/markerStore';
import { DownloadInfoItem } from '../../../../types/analytics';
import { DatasetType } from '../../../../types/marker';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { SEARCH_PARAM } from '../../../../utils/common';
import { withLoading } from '../../../common/Loading/withLoading';
import { IWmsProps } from '../../Map/Layer/DatasetMesh';

export const useDatasetChange = (
  item: DownloadInfoItem,
  namePanel: string,
  downloadItems: DownloadInfoItem[],
  onMeshControl: (value: string) => void,
  setMeshUrl: (item?: IWmsProps) => void
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const loadingContext = useContext(LoadingContext);

  const setIsChangeDatasetWarningVisible = useHomeStore(
    (state) => state.setIsChangeDatasetWarningVisible
  );
  const setTemporaryDatasetName = useHomeStore(
    (state) => state.setTemporaryDatasetName
  );
  const temporaryDatasetName = useHomeStore(
    (state) => state.temporaryDatasetName
  );
  const isChangeDatasetWarningVisible = useHomeStore(
    (state) => state.isChangeDatasetWarningVisible
  );
  const showMesh = useLegendStore((state) => state.showMesh);

  const setDownloadPage = useMarkerStore((state) => state.setDownloadPage);
  const setActivePanel = useMarkerStore((state) => state.setActivePanel);
  const point = useActiveMarker();
  const updateMarker = useMarkerStore((state) => state.updateMarker);
  const setActiveDatasetKey = useMarkerStore(
    (state) => state.setActiveDatasetKey
  );

  const enrichedMetadata = useAnalyticsStore((state) => state.enrichedMetadata);

  const handleActivePanel = useCallback(
    (markerId: string, datasetKey: string) => {
      setActivePanel(markerId, datasetKey);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set(SEARCH_PARAM.ACTIVE_PANEL, datasetKey);
      navigate(`/?${searchParams.toString()}`);
    },
    [location.search, navigate, setActivePanel]
  );

  const handleChangePanelCategory = useCallback(
    (item?: DatasetType | string | string[]) => {
      const itemString = Array.isArray(item) ? item[0] : item;
      if (point) {
        if (!itemString || itemString === '') {
          handleActivePanel(point.id, '');
        } else {
          handleActivePanel(point.id, itemString);
          setMeshUrl(undefined);

          const newDatasetId =
            point.downloadPage.find((e) => e.radioKey === itemString)
              ?.datasetId || point.dataset;
          setActiveDatasetKey(
            point.id,
            itemString as DatasetType,
            newDatasetId
          );
        }
      }
    },
    [point, setActiveDatasetKey, setMeshUrl, handleActivePanel]
  );

  const onDatasetValueChange = useCallback(
    (item: DownloadInfoItem) => {
      if (!point || !point.downloadPage) return;

      setDownloadPage(point.id, item.radioKey, item);
    },
    [point, setDownloadPage]
  );

  const changeDataset = useCallback(
    (newDataset: string) => {
      const findActiveDownload =
        point?.downloadPage.find(
          (item) => item.radioKey === point?.activeDatasetKey
        ) ?? item;

      const currentItem = {
        datasetId: findActiveDownload.datasetId,
        dateRange: findActiveDownload.dateRange,
        radioKey: findActiveDownload.radioKey,
        options: findActiveDownload.options,
      };

      downloadItems.forEach((element) => {
        const findDataset = element.options.find((dataset) => {
          return dataset.id === newDataset;
        });

        if (findDataset) {
          currentItem.radioKey = element.radioKey;
          currentItem.options = element.options;
          currentItem.dateRange = element.dateRange;
        }
      });

      handleChangePanelCategory(currentItem.radioKey);
      const obj = Object.assign({}, currentItem, { datasetId: newDataset });

      onDatasetValueChange(obj);

      if (point) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set(currentItem.radioKey, newDataset);
        navigate(`/?${searchParams.toString()}`);

        updateMarker(point.id, { dataset: newDataset });
        if (showMesh) {
          onMeshControl(newDataset);
        }
      }
    },
    [
      item,
      downloadItems,
      point,
      navigate,
      location,
      showMesh,
      onMeshControl,
      updateMarker,
      handleChangePanelCategory,
      onDatasetValueChange,
    ]
  );

  const onDatasetChange = useCallback(
    (value: string) => {
      const datasetTask = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === item.datasetId &&
          task.CoordinatesString === point?.coordinates.join()
      );

      if (datasetTask === undefined) {
        withLoading(() => changeDataset(value), loadingContext, {
          source: 'useDatasetChange - changeDataset',
        });
      } else {
        setTemporaryDatasetName(value);
        setIsChangeDatasetWarningVisible(true);
      }
    },
    [
      item.datasetId,
      point,
      enrichedMetadata,
      changeDataset,
      loadingContext,
      setTemporaryDatasetName,
      setIsChangeDatasetWarningVisible,
    ]
  );

  const handleChangeDatasetWarningOk = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    withLoading(
      () => changeDataset(temporaryDatasetName ?? ''),
      loadingContext,
      { source: 'useDatasetChange - handleChangeDatasetWarningOk' }
    );
    setTemporaryDatasetName('');
  }, [
    temporaryDatasetName,
    setIsChangeDatasetWarningVisible,
    changeDataset,
    loadingContext,
    setTemporaryDatasetName,
  ]);

  const handleChangeDatasetWarningCancel = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    setTemporaryDatasetName('');
  }, [setIsChangeDatasetWarningVisible, setTemporaryDatasetName]);

  const onDateChange = useCallback(
    (value: any) => {
      if (!item || !value) return;
      const obj = Object.assign({}, item, { dateRange: value });
      onDatasetValueChange(obj);
    },
    [item, onDatasetValueChange]
  );

  return {
    changeDataset,
    onDatasetChange,
    handleChangeDatasetWarningOk,
    handleChangeDatasetWarningCancel,
    onDateChange,
    onDatasetValueChange,
    handleChangePanelCategory,
    isChangeDatasetWarningVisible,
  };
};
